import { useState, useCallback, KeyboardEvent } from "react";

export function useZipCodeInput() {
  const [zipCodeInput, setZipCodeInput] = useState("");
  const [zipCodeList, setZipCodeList] = useState<string[]>([]);
  const [zipCodeError, setZipCodeError] = useState<string | null>(null);

  const validateZipCode = (zipCode: string): boolean => {
    return /^\d{5}$/.test(zipCode);
  };

  const handleAddZipCode = useCallback(() => {
    const trimmedZip = zipCodeInput.trim();

    if (!trimmedZip) {
      return;
    }

    const zipCodes = trimmedZip
      .split(",")
      .map((zip) => zip.trim())
      .filter((zip) => zip.length > 0);

    const validZips: string[] = [];
    const invalidZips: string[] = [];

    zipCodes.forEach((zip) => {
      if (validateZipCode(zip)) {
        if (!zipCodeList.includes(zip) && !validZips.includes(zip)) {
          validZips.push(zip);
        }
      } else {
        invalidZips.push(zip);
      }
    });

    if (invalidZips.length > 0) {
      setZipCodeError(
        `Invalid zip code${invalidZips.length > 1 ? "s" : ""}: ${invalidZips.join(", ")}. Please enter valid 5-digit zip codes.`,
      );
    } else {
      setZipCodeError(null);
    }

    if (validZips.length > 0) {
      setZipCodeList((prevList) => [...prevList, ...validZips]);
      setZipCodeInput("");
    }
  }, [zipCodeInput, zipCodeList]);

  const handleRemoveZipCode = useCallback((zipCodeToRemove: string) => {
    setZipCodeList((prevList) =>
      prevList.filter((zipCode) => zipCode !== zipCodeToRemove),
    );
  }, []);

  const appendZipCodes = useCallback(
    (zipCodes: string[]) => {
      const validZips = zipCodes.filter(
        (zip) => validateZipCode(zip) && !zipCodeList.includes(zip),
      );
      if (validZips.length > 0) {
        setZipCodeList((prevList) => {
          const uniqueNew = validZips.filter((zip) => !prevList.includes(zip));
          return [...prevList, ...uniqueNew];
        });
      }
    },
    [zipCodeList],
  );

  const handleClearAll = useCallback(() => {
    setZipCodeList([]);
    setZipCodeError(null);
  }, []);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleAddZipCode();
      }
    },
    [handleAddZipCode],
  );

  return {
    zipCodeInput,
    zipCodeList,
    zipCodeError,
    hasZipCodes: zipCodeList.length > 0,
    setZipCodeInput,
    handleAddZipCode,
    handleRemoveZipCode,
    handleClearAll,
    handleKeyDown,
    appendZipCodes,
  };
}
